/**
 * useSidebarData — owns the project + direct-chat list state, fetches
 * it from the services, and subscribes to the typed event bus for the
 * create/rename/delete/move lifecycle.
 *
 * Return shape exposes mutators (move/rename/delete) so subcomponents
 * don't have to re-implement them. Phase E1: extracted from Sidebar.tsx.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { emit as emitAppEvent, on as onAppEvent, type BusChatSummary } from '../../events/bus';
import { getProjects, deleteProjects, renameProject } from '../../../projectsService';
import {
    getChatMetadatasForProject,
    getProjectlessChats,
    type ChatMetadata,
} from '../../../modules/chat/chatMetadataService';
import { deleteChat, renameChat } from '../../../modules/chat/chatservice';
import { captureError } from '../../errors/captureError';
import type { Project } from './types';

type UseSidebarDataResult = {
    projects: Project[];
    directChats: ChatMetadata[];
    directChatsMoreUrl: string | null;
    isLoadingMoreDirectChats: boolean;
    loadMoreDirectChats: () => Promise<void>;
    animatingChatIds: Set<string>;
    clearAnimation: (id: string) => void;
    setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
    setDirectChats: React.Dispatch<React.SetStateAction<ChatMetadata[]>>;
    showCreateProjectModal: boolean;
    setShowCreateProjectModal: (v: boolean) => void;
    expandedProjects: Set<string>;
    toggleProject: (projectId: string) => void;
    expandProject: (projectId: string) => void;
    deleteProject: (projectId: string) => Promise<void>;
    deleteProjectChat: (projectId: string, chatId: string) => Promise<void>;
    deleteDirectChat: (chatId: string) => Promise<void>;
    renameProjectById: (projectId: string, name: string) => Promise<void>;
    renameChatById: (chatId: string, name: string, projectId?: string) => Promise<void>;
    isDeletingChatId: string | null;
    isDeletingProjectId: string | null;
    isDeletingProjectChatId: string | null;
};

type Callbacks = {
    selectedChatId?: string | undefined;
    onNewDirectChat?: (() => void) | undefined;
};

export function useSidebarData({ selectedChatId, onNewDirectChat }: Callbacks): UseSidebarDataResult {
    const [projects, setProjects] = useState<Project[]>([]);
    const [directChats, setDirectChats] = useState<ChatMetadata[]>([]);
    const [directChatsMoreUrl, setDirectChatsMoreUrl] = useState<string | null>(null);
    const [isLoadingMoreDirectChats, setIsLoadingMoreDirectChats] = useState(false);
    const [animatingChatIds, setAnimatingChatIds] = useState<Set<string>>(new Set());
    const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
    const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

    const [isDeletingChatId, setIsDeletingChatId] = useState<string | null>(null);
    const [isDeletingProjectId, setIsDeletingProjectId] = useState<string | null>(null);
    const [isDeletingProjectChatId, setIsDeletingProjectChatId] = useState<string | null>(null);

    const directChatsRef = useRef(directChats);
    directChatsRef.current = directChats;

    const loadDirectChatsFirstPage = useCallback(async () => {
        try {
            const response = await getProjectlessChats();
            setDirectChats((prev) => {
                if (prev.length === 0) {
                    setAnimatingChatIds((p) => {
                        const next = new Set(p);
                        response.chatMetadatas.forEach((c) => next.add(c.id));
                        return next;
                    });
                    return response.chatMetadatas;
                }
                const incomingById = new Map(response.chatMetadatas.map((c) => [c.id, c] as const));
                const updatedPrev = prev.map((p) => incomingById.get(p.id) ?? p);
                const seen = new Set(updatedPrev.map((c) => c.id));
                const appended = response.chatMetadatas.filter((c) => !seen.has(c.id));
                return [...updatedPrev, ...appended];
            });
            setDirectChatsMoreUrl(response.next ?? null);
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'loadDirectChatsFirstPage' });
        }
    }, []);

    useEffect(() => {
        async function loadProjects() {
            try {
                const projectsResponse = await getProjects();
                const projectsWithChats = await Promise.all(
                    projectsResponse.projects.map(async (project) => {
                        const chatsResponse = await getChatMetadatasForProject(project.id);
                        return {
                            id: project.id,
                            name: project.name,
                            description: project.description,
                            files: project.files,
                            chats: chatsResponse.chatMetadatas.map((chat) => ({
                                id: chat.id,
                                name: chat.name,
                                lastMessage: '',
                                updatedAt: 'Recently',
                            })),
                        };
                    }),
                );
                setProjects(projectsWithChats);
                // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
                emitAppEvent('sidebar-projects-loaded', {
                    projects: projectsWithChats.map((p) => ({
                        id: p.id,
                        name: p.name,
                        chats: p.chats,
                    })),
                });
                const allChatIds = projectsWithChats.flatMap((p) => p.chats.map((c) => c.id));
                setAnimatingChatIds((prev) => {
                    const next = new Set(prev);
                    allChatIds.forEach((id) => next.add(id));
                    return next;
                });
            } catch (err) {
                captureError(err, { scope: 'useSidebarData', op: 'loadProjects' });
            }
        }

        void loadProjects();
        void loadDirectChatsFirstPage();

        const bumpDirectChatToTop = (chatId: string) => {
            setDirectChats((prev) => {
                const idx = prev.findIndex((c) => c.id === chatId);
                if (idx <= 0) return prev;
                const chat = prev[idx];
                if (!chat) return prev;
                return [chat, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
        };

        const bumpProjectToTop = (projectId: string) => {
            setProjects((prev) => {
                const idx = prev.findIndex((p) => p.id === projectId);
                if (idx <= 0) return prev;
                const project = prev[idx];
                if (!project) return prev;
                return [project, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
            });
        };

        const handleDirectChatCreated = (payload: BusChatSummary) => {
            if (typeof payload.id === 'string') {
                const created: ChatMetadata = { id: payload.id, name: payload.name };
                setAnimatingChatIds((prev) => new Set(prev).add(created.id));
                setDirectChats((prev) => {
                    const without = prev.filter((c) => c.id !== created.id);
                    return [created, ...without];
                });
                void loadDirectChatsFirstPage();
                return;
            }
            void loadDirectChatsFirstPage();
        };

        const handleDirectChatTouched = (payload: { chatId: string }) => {
            bumpDirectChatToTop(payload.chatId);
        };

        const handleProjectChatCreated = (detail: { projectId: string; chat: { id: string; name: string } }) => {
            setAnimatingChatIds((prev) => new Set(prev).add(detail.chat.id));
            setProjects((prev) =>
                prev.map((p) =>
                    p.id === detail.projectId
                        ? {
                              ...p,
                              chats: [
                                  { id: detail.chat.id, name: detail.chat.name, updatedAt: 'Just now' },
                                  ...p.chats,
                              ],
                          }
                        : p,
                ),
            );
            bumpProjectToTop(detail.projectId);
            setExpandedProjects((prev) => new Set(prev).add(detail.projectId));
        };

        const handleProjectChatTouched = (detail: { projectId: string; chatId: string }) => {
            const { projectId, chatId } = detail;
            if (!projectId || !chatId) return;
            setProjects((prev) =>
                prev.map((p) => {
                    if (p.id !== projectId) return p;
                    const idx = p.chats.findIndex((c) => c.id === chatId);
                    if (idx <= 0) return p;
                    const chat = p.chats[idx];
                    if (!chat) return p;
                    return {
                        ...p,
                        chats: [chat, ...p.chats.slice(0, idx), ...p.chats.slice(idx + 1)],
                    };
                }),
            );
            bumpProjectToTop(projectId);
        };

        const handleOpenCreateProjectModal = () => setShowCreateProjectModal(true);

        const handleChatMoved = (detail: { chatId: string; fromProjectId: string | null; toProjectId: string | null }) => {
            const { chatId: movedChatId, fromProjectId, toProjectId } = detail;
            const now = new Date().toISOString();
            if (fromProjectId && toProjectId) {
                setProjects((prev) => {
                    const source = prev.find((p) => p.id === fromProjectId);
                    const chat = source?.chats.find((c) => c.id === movedChatId);
                    if (!chat) return prev;
                    return prev.map((p) => {
                        if (p.id === fromProjectId) return { ...p, chats: p.chats.filter((c) => c.id !== movedChatId) };
                        if (p.id === toProjectId) return { ...p, chats: [chat, ...p.chats] };
                        return p;
                    });
                });
            } else if (fromProjectId && !toProjectId) {
                setProjects((prev) => {
                    const source = prev.find((p) => p.id === fromProjectId);
                    const chat = source?.chats.find((c) => c.id === movedChatId);
                    if (chat) setDirectChats((dc) => [{ id: chat.id, name: chat.name, updatedAt: now }, ...dc]);
                    return prev.map((p) =>
                        p.id === fromProjectId
                            ? { ...p, chats: p.chats.filter((c) => c.id !== movedChatId) }
                            : p,
                    );
                });
            } else if (!fromProjectId && toProjectId) {
                const chat = directChatsRef.current.find((c) => c.id === movedChatId);
                if (chat) {
                    setDirectChats((prev) => prev.filter((c) => c.id !== movedChatId));
                    setProjects((prev) =>
                        prev.map((p) =>
                            p.id === toProjectId
                                ? { ...p, chats: [{ id: chat.id, name: chat.name, updatedAt: now }, ...p.chats] }
                                : p,
                        ),
                    );
                }
            }
        };

        const offs = [
            onAppEvent('direct-chat-created', handleDirectChatCreated),
            onAppEvent('direct-chat-touched', handleDirectChatTouched),
            // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
            onAppEvent('project-chat-created', handleProjectChatCreated),
            // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
            onAppEvent('project-chat-touched', handleProjectChatTouched),
            // eslint-disable-next-line chiefy/no-naked-concept-strings -- event-bus channel name (infrastructure id), not user copy.
            onAppEvent('open-create-project-modal', handleOpenCreateProjectModal),
            onAppEvent('chat-moved', handleChatMoved),
        ];
        return () => { for (const off of offs) off(); };
    }, [loadDirectChatsFirstPage]);

    const loadMoreDirectChats = useCallback(async () => {
        if (!directChatsMoreUrl || isLoadingMoreDirectChats) return;
        try {
            setIsLoadingMoreDirectChats(true);
            const res = await getProjectlessChats(directChatsMoreUrl);
            setDirectChats((prev) => {
                const seen = new Set(prev.map((c) => c.id));
                const merged = [...prev];
                for (const c of res.chatMetadatas) {
                    if (!seen.has(c.id)) merged.push(c);
                }
                return merged;
            });
            setDirectChatsMoreUrl(res.next);
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'loadMoreDirectChats' });
        } finally {
            setIsLoadingMoreDirectChats(false);
        }
    }, [directChatsMoreUrl, isLoadingMoreDirectChats]);

    const clearAnimation = useCallback((id: string) => {
        setAnimatingChatIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const toggleProject = useCallback((projectId: string) => {
        setExpandedProjects((prev) => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    }, []);

    const expandProject = useCallback((projectId: string) => {
        setExpandedProjects((prev) => new Set(prev).add(projectId));
    }, []);

    const deleteProject = useCallback(async (projectId: string) => {
        try {
            setIsDeletingProjectId(projectId);
            const deletedProject = projects.find((p) => p.id === projectId);
            await deleteProjects([projectId]);
            setProjects((prev) => prev.filter((p) => p.id !== projectId));
            setExpandedProjects((prev) => {
                const next = new Set(prev);
                next.delete(projectId);
                return next;
            });
            if (deletedProject && selectedChatId
                && deletedProject.chats.some((c) => c.id === selectedChatId)) {
                onNewDirectChat?.();
            }
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'deleteProject' });
        } finally {
            setIsDeletingProjectId(null);
        }
    }, [projects, selectedChatId, onNewDirectChat]);

    const deleteProjectChat = useCallback(async (projectId: string, chatId: string) => {
        try {
            setIsDeletingProjectChatId(chatId);
            await deleteChat(chatId);
            setProjects((prev) =>
                prev.map((p) =>
                    p.id === projectId
                        ? { ...p, chats: p.chats.filter((c) => c.id !== chatId) }
                        : p,
                ),
            );
            if (selectedChatId === chatId) onNewDirectChat?.();
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'deleteProjectChat' });
        } finally {
            setIsDeletingProjectChatId(null);
        }
    }, [selectedChatId, onNewDirectChat]);

    const deleteDirectChat = useCallback(async (chatId: string) => {
        try {
            setIsDeletingChatId(chatId);
            await deleteChat(chatId);
            setDirectChats((prev) => prev.filter((c) => c.id !== chatId));
            if (selectedChatId === chatId) onNewDirectChat?.();
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'deleteDirectChat' });
        } finally {
            setIsDeletingChatId(null);
        }
    }, [selectedChatId, onNewDirectChat]);

    const renameProjectById = useCallback(async (projectId: string, name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        try {
            await renameProject(projectId, trimmed);
            setProjects((prev) =>
                prev.map((p) => (p.id === projectId ? { ...p, name: trimmed } : p)),
            );
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'renameProjectById' });
        }
    }, []);

    const renameChatById = useCallback(async (chatId: string, name: string, projectId?: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        try {
            await renameChat(chatId, trimmed);
            if (projectId) {
                setProjects((prev) =>
                    prev.map((p) =>
                        p.id === projectId
                            ? { ...p, chats: p.chats.map((c) => (c.id === chatId ? { ...c, name: trimmed } : c)) }
                            : p,
                    ),
                );
            } else {
                setDirectChats((prev) =>
                    prev.map((c) => (c.id === chatId ? { ...c, name: trimmed } : c)),
                );
            }
        } catch (err) {
            captureError(err, { scope: 'useSidebarData', op: 'renameChatById' });
        }
    }, []);

    return {
        projects,
        directChats,
        directChatsMoreUrl,
        isLoadingMoreDirectChats,
        loadMoreDirectChats,
        animatingChatIds,
        clearAnimation,
        setProjects,
        setDirectChats,
        showCreateProjectModal,
        setShowCreateProjectModal,
        expandedProjects,
        toggleProject,
        expandProject,
        deleteProject,
        deleteProjectChat,
        deleteDirectChat,
        renameProjectById,
        renameChatById,
        isDeletingChatId,
        isDeletingProjectId,
        isDeletingProjectChatId,
    };
}
